import { forwardRef, type CSSProperties, type InputHTMLAttributes } from "react";
import { color, layout, radius, space, typography, motion as motionTokens } from "@tokens/index";

/**
 * TextInput.
 *
 * Variants:
 *  - `default` standalone input: 40px, `md` radius, hairline border. Focus
 *              is the text-field treatment from the global stylesheet
 *              (`data-focus-style="input"`): the edge turns to the accent at
 *              ~55% and a 3px halo at ~14% surrounds it. No outline.
 *  - `bare`    no chrome. The parent frame owns the border and focus ring.
 *
 * `error` sets `aria-invalid` and a danger border. Border colors live in the
 * stylesheet, not inline, so `:focus-visible` can change them.
 */

export type TextInputVariant = "default" | "bare";

export interface TextInputProps
    extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "style"> {
    readonly error?: boolean;
    readonly fullWidth?: boolean;
    readonly variant?: TextInputVariant;
    readonly style?: CSSProperties;
}

function baseStyleFor(variant: TextInputVariant): CSSProperties {
    if (variant === "bare") {
        return {
            display: "inline-flex",
            height: "100%",
            // The bare input keeps the 40px control floor so a composer
            // well never presents a sub-40px pointer target.
            minHeight: `${String(layout.hitArea)}px`,
            paddingInline: 0,
            paddingBlock: space[2],
            fontFamily: typography.family.system,
            fontSize: typography.scale.md,
            fontWeight: typography.weights.regular,
            lineHeight: 1.4,
            letterSpacing: typography.optical.md,
            color: color.fg.default,
            background: "transparent",
            border: "none",
            outline: "none",
            boxSizing: "border-box",
        };
    }
    return {
        display: "inline-flex",
        minHeight: "40px",
        paddingInline: space[3],
        paddingBlock: space[2],
        fontFamily: typography.family.system,
        fontSize: typography.scale.sm,
        fontWeight: typography.weights.regular,
        lineHeight: 1.2,
        letterSpacing: typography.optical.sm,
        color: color.fg.default,
        background: color.bg.surface,
        borderWidth: "1px",
        borderStyle: "solid",
        borderRadius: radius.md,
        boxSizing: "border-box",
        transitionProperty: "border-color, background, box-shadow",
        transitionDuration: motionTokens.durations.fast,
        transitionTimingFunction: motionTokens.easings.standard,
    };
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
    { error = false, fullWidth = false, disabled = false, variant = "default", style: styleOverride, ...rest },
    ref,
) {
    const computed: CSSProperties = {
        ...baseStyleFor(variant),
        ...(fullWidth ? { width: "100%" } : {}),
        cursor: disabled ? "not-allowed" : "text",
        opacity: disabled ? 0.56 : 1,
    };

    const style: CSSProperties =
        styleOverride !== undefined ? { ...computed, ...styleOverride } : computed;

    return (
        <input
            ref={ref}
            disabled={disabled}
            aria-invalid={error || undefined}
            data-focus-style={variant === "bare" ? "custom" : "input"}
            data-variant={variant}
            data-error={error || undefined}
            style={style}
            {...rest}
        />
    );
});

TextInput.displayName = "TextInput";
